<template>
  <div class="log-viewer">
    <div v-if="props.isPaused" class="log-viewer__pause-label">PAUSED</div>
    <div class="log-viewer__status-indicator" :class="connectionStatus" :title="statusText" />
    <button 
      v-if="logs"
      class="log-viewer__download-btn"
      @click="downloadLogs"
      title="Download logs as file"
    >
      <SVGIcon :iconName="'download'" :width="'16px'" :height="'16px'" />
      <span>Download</span>
    </button>
    <div ref="logContainer" class="log-viewer__container">
      <div v-if="!logs && connectionStatus !== 'connecting'" class="log-viewer__empty">
        <p>No logs available</p>
      </div>
      <pre v-else class="log-viewer__content" v-html="ansiToHtml(logs)"></pre>
    </div>
    <div v-if="error" class="log-viewer__error">
      <p>{{ error }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import LogsConnect from '@/assets/js/DatastoreFunctions/LogsConnect.js';
import SVGIcon from '@/components/SVGIcon.vue';
import ansiHtml from 'ansi-html-community';

const props = defineProps({
  taskId: {
    type: [String, Number],
    required: true,
  },
  isPaused: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update-task-info', 'update-connection-status']);

// Refs
const logContainer = ref(null);
const ws = ref(null);

// Reactive state
const logs = ref('');
const connectionStatus = ref('disconnected');
const error = ref(null);
const autoScroll = ref(true);
const taskInfo = ref(null);
const logBuffer = ref('');
const logsConnect = new LogsConnect();

// Computed properties
const statusText = computed(() => {
  const statusMap = {
    connecting: 'Connecting...',
    connected: 'Connected',
    disconnected: 'Disconnected',
    error: 'Connection Error',
  };
  return statusMap[connectionStatus.value] || 'Unknown';
});

const taskStatus = computed(() => {
  if (!taskInfo.value) return 'Unknown';
  return taskInfo.value.status || 'Unknown';
});

const taskProgress = computed(() => {
  if (!taskInfo.value || !taskInfo.value.status_info) return 0;
  return Math.round(taskInfo.value.status_info.percent || 0);
});

// Watch taskId changes
watch(() => props.taskId, (newId) => {
  disconnect();
  logs.value = '';
  logBuffer.value = '';
  taskInfo.value = null;
  fetchTaskInfo();
  connect();
});

// Watch isPaused changes - flush buffer when resuming
watch(() => props.isPaused, (newPausedState) => {
  if (!newPausedState && logBuffer.value) {
    logs.value += logBuffer.value;
    logBuffer.value = '';
    nextTick(() => {
      scrollToBottom();
    });
  }
});

// Lifecycle hooks
onMounted(() => {
  fetchTaskInfo();
  connect();
});

onBeforeUnmount(() => {
  disconnect();
});

// Methods
const fetchTaskInfo = async () => {
  // Task info is now fetched via WebSocket messages from server
};

const connect = () => {
  try {
    connectionStatus.value = 'connecting';
    error.value = null;

    const wsUrl = logsConnect.getUrl(`/logs-if/${props.taskId}`);
    ws.value = new WebSocket(wsUrl);

    ws.value.onopen = () => {
      connectionStatus.value = 'connected';
      error.value = null;
      emit('update-connection-status', connectionStatus.value);
    };

    ws.value.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        if (message.message_type === 'error') {
          error.value = message.message;
          return;
        }

        if (message.message_type === 'task' && message.task) {
          taskInfo.value = message.task;
          emit('update-task-info', taskInfo.value);
          return;
        }

        if (message.message_type === 'log') {
          addLog(message);
          return;
        }

      } catch (e) {
        console.error('Failed to parse message:', e);
      }
    };

    ws.value.onerror = (error) => {
      connectionStatus.value = 'error';
      error.value = 'WebSocket connection error';
      console.error('WebSocket error:', error);
      emit('update-connection-status', connectionStatus.value);
    };

    ws.value.onclose = () => {
      connectionStatus.value = 'disconnected';
      emit('update-connection-status', connectionStatus.value);
    };
  } catch (e) {
    connectionStatus.value = 'error';
    error.value = `Failed to connect: ${e.message}`;
    console.error('Connection error:', e);
    emit('update-connection-status', connectionStatus.value);
  }
};

const disconnect = () => {
  if (ws.value) {
    ws.value.close();
    ws.value = null;
    connectionStatus.value = 'disconnected';
  }
};

const addLog = (logMessage) => {
  // Get the message text - handle both string and object formats
  const messageText = typeof logMessage === 'string' 
    ? logMessage 
    : (logMessage.message || JSON.stringify(logMessage));
  
  const logLine = messageText + '\n';

  // If paused or autoScroll is off, add to buffer instead of display
  if (!autoScroll.value || props.isPaused) {
    logBuffer.value += logLine;
    return;
  }

  // If not paused, add to display logs
  logs.value += logLine;

  nextTick(() => {
    scrollToBottom();
  });
};

const scrollToBottom = () => {
  const container = logContainer.value;
  if (container) {
    container.scrollTop = container.scrollHeight;
  }
};

const formatTime = (timestamp) => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  } catch (e) {
    return timestamp;
  }
};

const toggleAutoScroll = () => {
  autoScroll.value = !autoScroll.value;
  
  // If resuming, flush the buffer to display
  if (autoScroll.value && logBuffer.value) {
    logs.value += logBuffer.value;
    logBuffer.value = '';
    scrollToBottom();
  }
};

const downloadLogs = () => {
  if (!logs.value) return;
  
  // Create a blob from the logs text
  const blob = new Blob([logs.value], { type: 'text/plain' });
  
  // Create a download link
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `logs-${props.taskId}-${new Date().toISOString().slice(0, 10)}.txt`;
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const ansiToHtml = (text) => {
  if (!text) return '';
  const html = ansiHtml(text);
  // Remove background color styles (handles both background: and background-color:)
  return html.replace(/\s*background(?:-color)?:\s*[^;'"]*;?/g, '');
};
</script>

<style lang="scss" scoped>
.log-viewer {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #1e1e1e;
  color: #d4d4d4;
  border-radius: 4px;
  overflow: hidden;
  font-family: 'Menlo', 'Monaco', 'Consolas', 'Courier New', monospace;
  position: relative;

  &__header {
    padding: 8px 12px;
    border-bottom: 1px solid #333;
    background: #252526;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__autoscroll-btn {
    padding: 6px 12px;
    background: #404040;
    border: 1px solid #555;
    color: #d4d4d4;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
 
    &:hover {
      background: #505050;
      border-color: #666;
    }

    &.is-active {
      background: #2e7d32;
      border-color: #3a9a40;
      color: #fff;
    }

    &:active {
      transform: scale(0.98);
    }
  }

  &__pause-label {
    position: absolute;
    top: 6px;
    right: 28px;
    color: #f44336;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    z-index: 10;
  }

  &__status-indicator {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #ccc;
    border: 2px solid #666;
    transition: background-color 0.2s ease;
    z-index: 10;

    &.connected {
      background: #4caf50;
      border-color: #388e3c;
      box-shadow: 0 0 8px rgba(76, 175, 80, 0.5);
    }

    &.connecting {
      background: #ff9800;
      border-color: #f57c00;
      animation: pulse 1.5s ease-in-out infinite;
    }

    &.error,
    &.disconnected {
      background: #f44336;
      border-color: #d32f2f;
      box-shadow: 0 0 8px rgba(244, 67, 54, 0.5);
    }
  }

  &__download-btn {
    position: absolute;
    top: 8px;
    right: 28px;
    padding: 6px 12px;
    background: #404040;
    border: 1px solid #555;
    color: #d4d4d4;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 6px;

    &:hover {
      background: #505050;
      border-color: #666;
      color: #ffffff;
    }

    &:active {
      transform: scale(0.98);
    }
  }

  &__header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 12px;

    h3 {
      margin: 0;
      font-size: 14px;
      font-weight: 600;
      flex-shrink: 0;
    }
  }

  &__header-right {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
  }

  &__task-status-header {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  &__status {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    flex-shrink: 0;
  }

  &__status-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;

    &.connecting {
      background-color: #ff9e64;
      animation: pulse 1.5s ease-in-out infinite;
    }

    &.connected {
      background-color: #9ece6a;
    }

    &.disconnected {
      background-color: #808080;
    }

    &.error {
      background-color: #f7768e;
    }
  }

  &__status-text {
    color: #858585;
  }

  &__task-uid {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    padding: 4px 8px;
    background: #2d2d2d;
    border-radius: 4px;
  }

  &__uid {
    color: #4ec9b0;
    font-weight: 500;
    font-family: 'Menlo', 'Monaco', 'Consolas', 'Courier New', monospace;
  }

  &__task-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid #404040;
  }

  &__task-info-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
  }

  &__label {
    color: #858585;
    font-weight: 600;
    min-width: 100px;
  }

  &__task-name {
    color: #9cdcfe;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__parent-link {
    color: #4ec9b0;
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: #646695;
      text-decoration: underline;
    }
  }

  &__status-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
  }

  &__status-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    white-space: nowrap;

    &.status-complete {
      background-color: #264f36;
      color: #9ece6a;
    }

    &.status-error {
      background-color: #3d2626;
      color: #f7768e;
    }

    &.status-terminated {
      background-color: #3d2f26;
      color: #deb894;
    }

    &.status-running,
    &.status-init,
    &.status-queued {
      background-color: #263352;
      color: #7aa3e5;
    }
  }

  &__progress {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__progress-fill {
    height: 100%;
    background-color: #2e7d32;
    border-radius: 2px;
    transition: width 0.3s ease;
  }

  &__progress-text {
    color: #858585;
    font-size: 11px;
    min-width: 35px;
    text-align: right;
  }

  &__progress-compact {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 100px;
    max-width: 200px;
  }

  &__progress-bar-compact {
    flex: 1;
    height: 3px;
    background-color: #333;
    border-radius: 2px;
    overflow: hidden;
  }

  &__progress-fill-compact {
    height: 100%;
    background-color: #2e7d32;
    border-radius: 2px;
    transition: width 0.3s ease;
  }

  &__progress-text-compact {
    color: #858585;
    font-size: 10px;
    min-width: 30px;
    text-align: right;
    flex-shrink: 0;
  }

  &__container {
    flex: 1;
    overflow-y: auto;
    padding: 12px 0;
    background: #1e1e1e;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #1e1e1e;
    }

    &::-webkit-scrollbar-thumb {
      background: #464647;
      border-radius: 4px;

      &:hover {
        background: #5a5a5a;
      }
    }
  }

  &__content {
    margin: 0;
    padding: 16px;
    font-size: 12px;
    line-height: 1.5;
    font-family: 'Menlo', 'Monaco', 'Consolas', 'Courier New', monospace;
    white-space: pre-wrap;
    word-break: break-word;
    overflow: auto;
  }

  &__line {
    display: flex;
    gap: 12px;
    padding: 0 16px;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: 'Menlo', 'Monaco', 'Consolas', 'Courier New', monospace;

    &--debug {
      color: #858585;
    }

    &--info {
      color: #858585;
    }

    &--warn,
    &--warning {
      color: #deb894;
    }

    &--error,
    &--err {
      color: #f7768e;
    }

    &--success {
      color: #9ece6a;
    }
  }

  &__timestamp {
    flex-shrink: 0;
    color: #646695;
    min-width: 60px;
    font-family: 'Menlo', 'Monaco', 'Consolas', 'Courier New', monospace;
  }

  &__level {
    flex-shrink: 0;
    font-weight: 600;
    min-width: 50px;
    text-transform: uppercase;
    font-family: 'Menlo', 'Monaco', 'Consolas', 'Courier New', monospace;
  }

  &__source {
    flex-shrink: 0;
    color: #9cdcfe;
    font-family: 'Menlo', 'Monaco', 'Consolas', 'Courier New', monospace;
  }

  &__message {
    flex: 1;
    word-break: break-all;
    font-family: 'Menlo', 'Monaco', 'Consolas', 'Courier New', monospace;
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #858585;
    font-family: 'Menlo', 'Monaco', 'Consolas', 'Courier New', monospace;

    p {
      margin: 0;
    }
  }

  &__error {
    padding: 12px 16px;
    background: #3d2626;
    border-top: 1px solid #7d3c2e;
    color: #f7768e;
    font-size: 12px;
    font-family: 'Menlo', 'Monaco', 'Consolas', 'Courier New', monospace;

    p {
      margin: 0;
    }
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
